import { useId } from "react";
import { FormControlContext } from "./FormControlContext";
import type { FormControlProps } from "./FormControlProps";
import classes from "./FormControl.module.scss";
import { clsx } from "clsx";
import { Required } from "@/components/ui/Icon/Required";
import { CheckSharp } from "@/components/ui/Icon/CheckSharp";

export function FormControl(props: FormControlProps) {
  const {
    htmlFor: htmlForProp,
    label,
    required,
    helperText,
    children,
    error,
    className,
    isValid,
    autoFit,
    type = "default",
  } = props;
  const defaultId = useId();

  const id = htmlForProp ?? defaultId;

  const Root = type === "fieldset" ? "fieldset" : "div";

  return (
    <FormControlContext.Provider
      value={{ id, hasError: !!error, isValid: isValid ?? undefined }}
    >
      <Root role="group" className={clsx(classes.root, className)}>
        {label && (
          <>
            {type === "fieldset" ? (
              <legend className={classes.label}>
                {label}
                {required && (
                  <Required
                    size={10}
                    className={classes.required}
                    aria-label="required"
                  />
                )}
              </legend>
            ) : (
              <label htmlFor={id} className={classes.label}>
                {label}
                {required && (
                  <Required
                    size={10}
                    className={classes.required}
                    aria-label="required"
                  />
                )}
              </label>
            )}
          </>
        )}

        <div className={clsx(classes.inputWrapper, autoFit && classes.autoFit)}>
          {children}

          {isValid && !error && (
            <div className={classes.checkWrapper}>
              <CheckSharp size={24} strokeWidth={4} stroke="currentColor" />
            </div>
          )}
        </div>

        {!error && helperText && (
          <div className={clsx(classes.helperText, classes.smallText)}>
            {helperText}
          </div>
        )}
        {error && (
          <div
            aria-live="polite"
            className={clsx(classes.error, classes.smallText)}
          >
            {error}
          </div>
        )}
      </Root>
    </FormControlContext.Provider>
  );
}
